<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

import { useCurrentStudyStore } from '../store/dicom-web/current-study-store';
import { useOrthancStore } from '../store/dicom-web/orthanc-store';

type TaskStatus = 'pending' | 'done' | 'failed' | 'timeout';

// A segmentation task with no terminal status after this long is shown as timed out.
const TASK_TIMEOUT_MS = 5 * 60 * 1000;
const CLOCK_TICK_MS = 30000;

const orthancStore = useOrthancStore();
const currentStudyStore = useCurrentStudyStore();

// Reactive clock so a stuck (never-updated) task can still transition to
// `timeout` in the UI without a server-side change. Study data itself is
// refreshed by the store's status polling (pollOrthancStatus -> pollStudies).
const now = ref(Date.now());
let clockTimer: ReturnType<typeof setInterval> | undefined;

function parseOrthancDate(s: string): Date {
  // "20260317T143207" → "2026-03-17T14:32:07"
  const iso = s.replace(
    /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})/,
    '$1-$2-$3T$4:$5:$6',
  );
  return new Date(iso);
}

const lastChangeMessage = computed(() => {
  const { lastChange } = orthancStore;
  if (!lastChange) return '';

  return `${lastChange.ChangeType}
        - ${lastChange.ResourceType}
        - ${parseOrthancDate(lastChange.Date).toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false,
        })}`;
});

// The Orthanc study matching the study currently open in the viewer.
const currentStudy = computed(() => {
  const uid = currentStudyStore.getStudyID;
  if (!uid) return undefined;
  return orthancStore.studies.find(
    s => s.MainDicomTags?.StudyInstanceUID === uid,
  );
});

// Server-side metadata calculation status (Orthanc metadata OCG19_MetadataStatus)
const metadataStatus = computed<'pending' | 'done' | 'failed' | null>(() => {
  const study = currentStudy.value;
  if (!study) return null;
  const raw = study.Metadata?.OCG19_MetadataStatus;
  if (raw === 'pending' || raw === 'calculating') return 'pending';
  if (raw === 'done') return 'done';
  if (raw === 'failed') return 'failed';
  return null;
});

const metadataIcon = computed(() => {
  const m = metadataStatus.value;
  if (m === 'pending') return 'fa-solid fa-spinner fa-spin';
  if (m === 'failed') return 'fa-solid fa-circle-check';
  return 'fa-solid fa-circle-check';
});

const metadataLabel = computed(() => {
  const m = metadataStatus.value;
  if (m === 'pending') return 'Calculating metadata...';
  if (m === 'failed') return 'Metadata error';
  return 'Metadata ready';
});

const metadataClass = computed(() => {
  const m = metadataStatus.value;
  return {
    'status-pending': m === 'pending',
    'status-failed': m === 'failed',
    'status-done': m === 'done',
  };
});

// Server-side segmentation status (Orthanc metadata OCG19_SegmentationStatus)
// of the open study, mirroring the logic used in the study list.
const segmentationStatus = computed<TaskStatus | null>(() => {
  const study = currentStudy.value;
  if (!study) return null;

  const raw = study.Metadata?.OCG19_SegmentationStatus;
  if (!raw) return null;
  if (raw === 'done') return 'done';
  if (raw === 'failed') return 'failed';
  if (raw === 'pending') {
    const lastUpdateStr = study.LastUpdate ?? study.Metadata?.LastUpdate ?? '';
    const lastMs = parseOrthancDate(lastUpdateStr).getTime();
    const timedOut = lastMs > 0 && now.value - lastMs > TASK_TIMEOUT_MS;
    return timedOut ? 'timeout' : 'pending';
  }
  return null;
});

const segmentationIcon = computed(() => {
  const s = segmentationStatus.value;
  if (s === 'done') return 'fa-solid fa-circle-check';
  if (s === 'pending') return 'fa-solid fa-clock';
  return 'fa-solid fa-circle-check'; // failed | timeout (gray tick)
});

const segmentationLabel = computed(() => {
  const s = segmentationStatus.value;
  if (s === 'done') return 'Segmentation done';
  if (s === 'failed') return 'Segmentation failed';
  if (s === 'timeout') return 'Segmentation timed out';
  if (s === 'pending') return 'Segmenting...';
  return `Background processing ${s}`;
});

const segmentationClass = computed(() => {
  const s = segmentationStatus.value;
  return {
    'status-done': s === 'done',
    'status-pending': s === 'pending',
    'status-failed': s === 'failed' || s === 'timeout',
  };
});

onMounted(() => {
  orthancStore.connect();
  orthancStore.startStatusPolling();
  clockTimer = setInterval(() => {
    now.value = Date.now();
  }, CLOCK_TICK_MS);
});

onBeforeUnmount(() => {
  orthancStore.stopStatusPolling();
  if (clockTimer) clearInterval(clockTimer);
});
</script>

<template>
  <div class="status-bar">
    <div class="status-content">
      <template v-if="orthancStore.isConnected">
        <i class="fa-solid fa-circle fa-2xs connected" />
        <span>Connected</span>
      </template>
      <template v-else-if="orthancStore.connectionState === 'connecting'">
        <i class="fa-solid fa-spinner fa-spin fa-xs" />
        <span>Connecting...</span>
      </template>
      <template v-else-if="orthancStore.connectionState === 'error'">
        <i class="fa-solid fa-circle-exclamation fa-xs custom-error" />
        <span>{{ orthancStore.connectionError || 'Connection failed' }}</span>
      </template>
      <template v-else>
        <i class="fa-solid fa-circle fa-2xs idle" />
        <span>Idle</span>
      </template>
      <div class="flex items-center gap-2 ml-auto">
        <template v-if="metadataStatus && metadataStatus !== 'done'">
          <i class="fa-xs" :class="[metadataIcon, metadataClass]" />
          <span :class="metadataClass">{{ metadataLabel }}</span>
        </template>
        <template v-if="segmentationStatus">
          <i class="fa-xs" :class="[segmentationIcon, segmentationClass]" />
          <span :class="segmentationClass">{{ segmentationLabel }}</span>
        </template>
        <template v-if="orthancStore.statusMessage">
          <i class="fa-solid fa-bell fa-xs" />
          <span>{{ orthancStore.statusMessage }}</span>
        </template>
        <template v-if="lastChangeMessage">
          <i class="fa-solid fa-circle-info fa-xs" />
          <span>{{ lastChangeMessage }}</span>
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped>
.status-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: 24px;
  background: var(--md-bg);
  border-top: 1px solid var(--md-border, rgba(128, 128, 128, 0.2));
  display: flex;
  align-items: center;
  padding: 0 12px;
  z-index: 9999;
}

.status-content {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.68rem;
  color: var(--md-text-secondary);
  width: 100%;
}

.connected {
  color: #1d9e75;
  font-size: 0.4rem;
}

.custom-error {
  color: var(--md-error, #ef4444);
}

.idle {
  color: #888;
  font-size: 0.4rem;
}

.status-done {
  color: #1d9e75;
}

.status-pending {
  color: var(--md-text-secondary);
}

.status-failed {
  color: rgba(136, 135, 128, 0.75);
}
</style>
